import CompanyDetails from '../mixins/CompanyDetails';
import OpeningHours from '../mixins/OpeningHours';
import Archived from '../mixins/Archived';
import Base from './Base';
import Interests from '../mixins/Interests';
import Website from '../mixins/Website';

export default class CreditCard extends Website(Interests(Archived(OpeningHours(CompanyDetails(Base))))) {

    // Title
    get title() {
        return this._data.title;
    }

    // Payment network
    get paymentNetwork() {
        return this._data.payment_network;
    }

    // Card types
    get cardType() {
        return this._data.card_type;
    }

    get isCredit() {
        return this._data.card_type == 'credit';
    }

    get isDebit() {
        return this._data.card_type == 'debit';
    }

    get isAggregator() {
        return this._data.card_type == 'aggregator';
    }

    // Is corporate card
    get isCorporateCard() {
        return this._data.is_corporate_card;
    }

    // Max credit
    get hasUnlimitedCredit() {
        return this._data.unlimited_credit;
    }

    get maxCredit() {
        return this._data.max_credit;
    }

    // yearly costs
    get hasFirstYearOffer() {
        return this._data.has_first_year_offer;
    }

    get firstYearCost() {
        if(this.hasFirstYearOffer) {
            return this._data.first_year_cost;
        }

        return null;
    }

    get yearlyCost() {
        const firstYearCost = this.firstYearCost;
        if(firstYearCost) {
            return firstYearCost;
        }

        return this._data.yearly_cost;
    }

    get yearlyCostString() {
        return this.formatCurrency(this.yearlyCost);
    }

    get yearlyCostHtml() {
        if(!this.hasFirstYearOffer) {
            return this.yearlyCostString;
        }

        return `${this.formatCurrency(this.firstYearCost)} <span class='standard-yearly-cost'>${this.formatCurrency(this._data.yearly_cost)}</span>`;
    }

    // min age 
    get minAge() {
        return this._data.min_age;
    }

    // Currency exchange fee
    get currencyExchangeFeeType() {
        return this._data.currency_exchange_fee_type;
    }

    get currencyExchangeFeePercentage() {
        return this._data.currency_exchange_fee_percentage;
    }

    get currencyExchangeFeeAmount() {
        return this._data.currency_exchange_fee_amount;
    }

    get currencyExchangeFeeString() {
        return 'Deprecated';
    }

    // withdrawal fee
    get withdrawalFeeType() {
        return this._data.withdrawal_fee_type;
    }  

    get withdrawalFeeString() {
        return 'Deprecated';
    }

    get withdrawalFeePercentage() {
        return this._data.withdrawal_fee_percentage;
    }

    get withdrawalFeeFixed() {
        return this._data.withdrawal_fee_fixed;
    }

    get withdrawalFeeMin() {
        return this._data.withdrawal_fee_min;
    }

    //requirements
    get acceptsNoIncome() {
        return this._data.accepts_no_income;
    }

    get minimumYearlyIncome() {
        return this._data.min_yearly_income;
    }

    // bonus, cashback, discoutns

    get hasBonusProgram() {
        return this._data.has_bonus_program;
    }

    get hasWelcomeOffer() {
        return this._data.welcome_offer_type && this._data.welcome_offer_type !== 'false';
    }

    get hasAirlineBonus() {
        return this._data.has_airline_bonus;
    }

    get hasFuelOffers() {
        return this._data.has_fuel_offers;
    }

    get fuelOffersChain() {
        return this._data.fuel_offers_chain;
    }

    get fuelDiscountManned() {
        return this._data.fuel_offers_discount_manned;
    }

    getFuelDiscountMannedString(template = '{discount} öre / liter') {
        if(this.hasFuelOffers && this.fuelDiscountManned) {
            return template.replace('{discount}', this.fuelDiscountManned);
        }

        return '';
    }

    get fuelDiscountUnmanned() {
        return this._data.fuel_offers_discount_unmanned;
    }

    getFuelDiscountUnmannedString(template = '{discount} öre / liter') {
        if(this.hasFuelOffers && this.fuelDiscountUnmanned) {
            return template.replace('{discount}', this.fuelDiscountUnmanned);
        }

        return '';
    }

    get hasCashback() {
        return this._data.has_cashback;
    }

    get cashbackPercentage() {
        return this._data.cashback_percentage;
    }

    get cashbackLimitType() {
        return this._data.cashback_limit_type;
    }

    get hasUnlimitedCashback() {
        return this.cashbackLimitType === 'unlimited_cashback';
    }

    get hasMonthlyCashbackLimit() {
        return this.cashbackLimitType === 'monthly';
    }

    get hasYearlyCashbackLimit() {
        return this.cashbackLimitType === 'yearly';
    }

    get cashbackLimitAmount() {
        return this._data.cashback_limit_amount;
    }

    getCashbackLimitString(monthShort = 'mån', yearShort = 'år') {

        if(this.hasUnlimitedCashback) {
            return '-';
        }
        
        return `${this.formatCurrency(this.cashbackLimitAmount)} / 
            ${this.hasMonthlyCashbackLimit ? monthShort : yearShort}`;
    } 

    get monthlyCashbackLimit() {
        return this.hasMonthlyCashbackLimit ? this.cashbackLimitAmount : 0;
    }

    get monthlyCashbackLimitString() {
        if (! this.hasMonthlyCashbackLimit) {
            return '-';
        }

        return this.formatCurrency(this.monthlyCashbackLimit);
    }

    get yearlyCashbackLimit() {
        return this.hasYearlyCashbackLimit ? this.cashbackLimitAmount : 0;
    }

    get yearlyCashbackLimitString() {
        if (! this.hasYearlyCashbackLimit) {
            return '-';
        }
        
        return this.formatCurrency(this.yearlyCashbackLimit);   
    }

    get hasDiscountProgram() {
        return this._data.has_discount_program;
    }

    get discountProgramName() {
        return this._data.discount_program_name;
    }

    get maxDiscountPercentage() {
        return this._data.discount_percentage_max;
    }

    get maxDiscountPercentageString() {
        return this.formatPercentage(this.maxDiscountPercentage);
    }

    get discountStoreCount() {
        return this._data.discount_store_count;
    }

    getDiscountString(template = 'Upp till {percentage} i {count} butiker') {
        if(this.hasDiscountProgram) {
            return template
                .replace('{percentage}', this.maxDiscountPercentageString)
                .replace('{count}', this.formatNumber(this.discountStoreCount));
        }

        return '';
    }

    get hasTravelDiscounts() {
        return this._data.has_travel_discounts;
    }

    get hasHotelDiscounts() {
        return this._data.has_hotel_discounts;
    }

    get hasRentalCarDiscounts() {
        return this._data.has_rental_car_discounts;
    }

    // lounge and concierge

    get hasConciergeService() {
        return this._data.has_concierge_service;
    }

    get hasLoungeAccess() {
        return this._data.has_lounge_access;
    }

    get hasPriorityPass() {
        return this._data.has_priority_pass !== 'false';
    }

    get priorityPassYearlyFreeVisits() {
        return this._data.priority_pass_yearly_free_visits;
    }

    get hasLoungeKey() {
        return this._data.has_lounge_key !== 'false';
    }

    get loungeKeyYearlyFreeVisits() {
        return this._data.lounge_key_yearly_free_visits;
    }

    // digital pay
    get hasApplePay() {
        return this._data.has_apple_pay;
    }

    get hasGooglePay() {
        return this._data.has_google_pay;
    }

    get hasSamsungPay() {
        return this._data.has_samsung_pay;
    }

    get hasSavingsInterest() {
        return this._data.has_savings_interest;
    }

    get savingsInterest() {
        return this._data.savings_interest;
    }

    get savingsInterestString() {
        return this.formatPercentage(this.savingsInterest);
    }

    // insurances
    get hasTravelInsurance() {
        return this._data.has_travel_insurance;
    }

    get hasPriceGuarantee() {
        return this._data.has_price_guarantee;
    }

    get hasCancellationProtection() {
        return this._data.has_cancellation_protection;
    }

    get hasDeductibleInsurance() {
        return this._data.has_deductible_insurance;
    }

    get hasComprehensiveInsurance() {
        return this._data.has_comprehensive_insurance;
    }

    get hasPurchaseProtection() {
        return this._data.has_purchase_protection;
    }

    get hasDeliveryInsurance() {
        return this._data.has_delivery_insurance;
    }

    get hasExtendedWarranty() {
        return this._data.has_extended_warranty;
    }

    get hasHoleInOneInsurance() {
        return this._data.has_hole_in_one_insurance;
    }

    get hasLostKeyInsurance() {
        return this._data.has_lost_key_insurance;
    }

    get hasWrongFuelInsurance() {
        return this._data.has_wrong_fuel_insurance;
    }

    get hasIdTheftInsurance() {
        return this._data.has_id_theft_insurance;
    }

    // compliance
    get complianceExample() {
        return this._data.compliance_example;
    }

    get sekkiUrl() {
        return this._data.compliance_sekki_url;
    }

    // SWEDEN SWEDEN

    get interestFreeDays() {
        return this._data.interest_free_days;
    }
    
    get acceptsPaymentRemarks() {
        return this._data.accepts_payment_remarks;
    }

    get aviFeePaperInvoice() {
        return this._data.avi_fee_paper_invoice;
    }

    get aviFeeEInvoice() {
        return this._data.avi_fee_e_invoice;
    }

    get aviFeeString() {
        return 'Deprecated';
    }

    get invoiceMinToPayPercentage() {
        return this._data.invoice_min_to_pay_percentage;
    }

    get invoiceMinToPaySum() {
        return this._data.invoice_min_to_pay_sum;
    }

    // GERMANY GERMANY
    
    get hasVideoIdentification() {
        return this._data.has_video_identification;
    }

    get hasPostalIdentification() {
        return this._data.has_postal_identification;
    }

    get withoutSchufa() {
        return this._data.without_schufa;
    }

    get cardColor () {
        return this._data.card_color;
    }

    get isColorBlack() {
        return this.cardColor === 'black';
    }

    get isColorPlatinum() {
        return this.cardColor === 'platinum';
    }

    get isColorGold() {
        return this.cardColor === 'gold';
    }


    // TODO
    get invoiceMinToPayString() {
        return 'Deprecated';
    }

    get overdraftFee() {
        return this._data.overdraft_fee;
    }

    get overdraftFeeString() {
        return this.formatCurrency(this.overdraftFee);
    }

    get latePaymentFee() {
        return this._data.late_payment_fee;
    }

    get latePaymentFeeString() {
        return this.formatCurrency(this.latePaymentFee);
    }

    get reminderFee() {
        return this._data.reminder_fee;
    }

    get reminderFeeString() {
        return this.formatCurrency(this.reminderFee);
    }

    get debtCollectionFee() {
        return this._data.debt_collection_fee;
    }

    get debtCollectionFeeString() {
        return this.formatCurrency(this.debtCollectionFee);
    }
    
    get penaltyInterest() {
        return this._data.penalty_interest;
    }

    get penaltyInterestString() {
        return this.formatPercentage(this.penaltyInterest, 2); 
    }

    /**
     * @param unlimited the unlimited string 
     * @returns 
     */
    getMaxCreditString(unlimited = 'obegränsad') {

        if(this.hasUnlimitedCredit) {
            return unlimited;
        }
        return this.formatCurrency(this.maxCredit);
    }

    /** 
     * @param template should include {days} that will be replaced
     * @returns String
     */
     getInterestFreeDaysString(template = '{days} dagar') {
        return template.replace('{days}', this.interestFreeDays);
    }

    /**
     * @param template should include {paperInvoiceFee} and {eInvoiceFee}
     * @returns String
     */
    getAviFeeString(template = '{paperInvoiceFee} ({eInvoiceFee} e-faktura)') {

        if(this.aviFeeEInvoice == 0 && this.aviFeePaperInvoice == 0) {
            return this.formatCurrency(0);
        }

        if(this.aviFeeEInvoice == this.aviFeePaperInvoice) {
            return this.formatCurrency(this.aviFeePaperInvoice);
        }

        return template
            .replace('{paperInvoiceFee}', this.formatCurrency(this.aviFeePaperInvoice))
            .replace('{eInvoiceFee}', this.formatCurrency(this.aviFeeEInvoice));
    }

    /**
     * 
     * @param template should include {percentage} and {amount}
     * @returns String
     */
    getInvoiceMinToPayString(template = '{percentage} (min {amount})') {
        return template
            .replace('{percentage}', this.formatPercentage(this.invoiceMinToPayPercentage, 2))
            .replace('{amount}', this.formatCurrency(this.invoiceMinToPaySum));
    }

    /**
     * 
     * @param free should be string if its free
     * @param templatePercentageAndMin should include {percentage} and {amount}
     * @param templatePercentageAndFixed should include {percentage} and {amount}
     */
    getWithdrawalFeeString(templatePercentageAndMin = '{percentage} (min {amount})', templatePercentageAndFixed = '{percentage} + {amount}') {
        switch(this.withdrawalFeeType) {
            case 'free': 
                return this.formatPercentage(0);

            case 'fixed_percentage': 
                return this.formatPercentage(this.withdrawalFeePercentage, 2);

            case 'fixed_sum': 
                return this.formatCurrency(this.withdrawalFeeFixed);

            case 'percentage_and_min': 
                return templatePercentageAndMin
                    .replace('{percentage}', this.formatPercentage(this.withdrawalFeePercentage, 2))
                    .replace('{amount}', this.formatCurrency(this.withdrawalFeeMin));

            case 'percentage_and_fixed': 
                return templatePercentageAndFixed
                    .replace('{percentage}', this.formatPercentage(this.withdrawalFeePercentage, 2))
                    .replace('{amount}', this.formatCurrency(this.withdrawalFeeFixed));
        }
    }

    getCurrencyExchangeFeeString() {
        switch(this.currencyExchangeFeeType) {
            case 'free': 
                return this.formatPercentage(0);

            case 'fixed_percentage': 
                return this.formatPercentage(this.currencyExchangeFeePercentage, 2);

            /*
            case 'fixed_sum': 
                return this.formatCurrency(this.currencyExchangeFeeAmount);

            case 'percentage_and_min': 
                return templatePercentageAndMin
                    .replace('{percentage}', this.formatPercentage(this.currencyExchangeFeePercentage))
                    .replace('{amount}', this.formatCurrency(this.currencyExchangeFeeAmount));

            case 'percentage_and_fixed': 
                return templatePercentageAndFixed
                .replace('{percentage}', this.formatPercentage(this.currencyExchangeFeePercentage))
                .replace('{amount}', this.formatCurrency(this.currencyExchangeFeeAmount));*/
        }
    }
    
    
}